import { useState, useEffect, useCallback, useMemo } from "react";
import { Modal } from "@/components/common/Modal";
import { Button } from "@/components/common/Button";
import { Badge } from "@/components/common/Badge";
import { Loader2, Check, Eye } from "lucide-react";
import { useTranslation } from "react-i18next";
import { tokenResolutionApi } from "@/services/api/tokenResolution";
import { listSubjects, listBackgrounds } from "@/services/api/qualification";
import type {
  SubjectSuggestion,
  BackgroundSuggestion,
  PdfPageResponse,
} from "@/types/tokenResolution";
import { extractErrorMessage } from "@/utils/errorHandler";
import { PdfPageViewer } from "@/components/pdf/PdfPageViewer";
import { loadTokenPdf } from "@/components/timetable-import/loadTokenPdf";

interface SubjectPdfPositions {
  sessions?: string[];
  pages?: number[];
}

interface UnmatchedSubjectDialogProps {
  open: boolean;
  token: string;
  tokens?: string[];
  hasQualificationMatrix: boolean;
  sessionId?: string;
  templateId?: string;
  positions?: SubjectPdfPositions;
  onResolve: (
    resolutionType: "alias" | "new_subject" | "exact_match",
    data: Record<string, unknown>
  ) => void;
  onClose: () => void;
}

export function UnmatchedSubjectDialog({
  open,
  token,
  tokens,
  hasQualificationMatrix,
  sessionId,
  templateId,
  positions,
  onResolve,
  onClose,
}: UnmatchedSubjectDialogProps) {
  const { t } = useTranslation();
  const allTokens = useMemo(() => {
    const list = tokens && tokens.length > 0 ? tokens : [token];
    return [...new Set(list.filter(Boolean))];
  }, [tokens, token]);
  const sessionIds = useMemo(() => {
    const fromPos = [...new Set((positions?.sessions || []).filter(Boolean))];
    if (sessionId && !fromPos.includes(sessionId))
      return [sessionId, ...fromPos];
    if (fromPos.length) return fromPos;
    return sessionId ? [sessionId] : [];
  }, [positions, sessionId]);
  const canPreviewPdf = sessionIds.length > 0 || Boolean(templateId);

  const sourceLabel = useCallback(
    (page: PdfPageResponse, index: number, total: number) => {
      const parts: string[] = [];
      if (total > 1) parts.push(`${index + 1}/${total}`);
      if (page.section_identifier) parts.push(page.section_identifier);
      if (page.source_filename) parts.push(page.source_filename);
      else if (!page.section_identifier) parts.push("Stundenplan");
      parts.push(`Seite ${page.page_number}`);
      return `«${token}» in ${parts.join(" · ")}`;
    },
    [token]
  );

  const loadPdfAt = useCallback(
    async (index: number) => {
      const sourceSession = sessionIds[index] || sessionId;
      setPdfLoading(true);
      setPdfError(null);
      try {
        const response = await loadTokenPdf({
          token,
          sessionId,
          templateId,
          sourceSessionId: sourceSession,
        });
        if (!response?.pdf_base64) {
          setPdfError("PDF konnte nicht geladen werden");
          return;
        }
        setPdf(response);
        setOccurrence(index);
        setPdfOpen(true);
      } catch (err) {
        setPdfError(
          extractErrorMessage(err, {
            loadError: "PDF konnte nicht geladen werden",
            saveError: "PDF konnte nicht geladen werden",
          })
        );
      } finally {
        setPdfLoading(false);
      }
    },
    [sessionIds, sessionId, templateId, token]
  );
  const [side, setSide] = useState<"alias" | "new">("alias");
  const [subjectSuggestions, setSubjectSuggestions] = useState<
    SubjectSuggestion[]
  >([]);
  const [backgroundSuggestions, setBackgroundSuggestions] = useState<
    BackgroundSuggestion[]
  >([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);
  const [loadingBackgrounds, setLoadingBackgrounds] = useState(false);
  const [selectedSubjectId, setSelectedSubjectId] = useState<string | null>(
    null
  );
  const [selectedBackgroundIds, setSelectedBackgroundIds] = useState<
    Set<string>
  >(new Set());
  const [subjectName, setSubjectName] = useState(token);
  const [subjectQuery, setSubjectQuery] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exactMatchSubject, setExactMatchSubject] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdf, setPdf] = useState<PdfPageResponse | null>(null);
  const [occurrence, setOccurrence] = useState(0);

  const loadSuggestions = useCallback(async () => {
    setLoadingSubjects(true);
    setLoadingBackgrounds(true);

    try {
      const allSubjects = await listSubjects();
      const tokenLower = token.toLowerCase();

      const subjects: SubjectSuggestion[] = allSubjects.map((s) => {
        const nameLower = s.name.toLowerCase();
        const codeLower = (s.code || "").toLowerCase();

        let score = 0;
        if (nameLower === tokenLower || codeLower === tokenLower) {
          score = 100;
        } else if (
          nameLower.includes(tokenLower) ||
          tokenLower.includes(nameLower)
        ) {
          score = 90;
        } else if (
          codeLower &&
          (codeLower.includes(tokenLower) || tokenLower.includes(codeLower))
        ) {
          score = 85;
        } else if (
          tokenLower.split(/\s+/).some((word) => nameLower.includes(word))
        ) {
          score = 70;
        }

        return {
          subject_id: s.id,
          name: s.name,
          code: s.code,
          similarity_score: score,
          is_preselected: false,
          is_alias: false,
        };
      });

      const sorted = subjects.sort(
        (a, b) => b.similarity_score - a.similarity_score
      );
      const bestMatch = sorted.find((s) => s.similarity_score >= 70);
      if (bestMatch) {
        bestMatch.is_preselected = true;
        setSelectedSubjectId(bestMatch.subject_id);
      }

      const exactMatch = allSubjects.find(
        (s) =>
          s.name.toLowerCase() === tokenLower ||
          (s.code && s.code.toLowerCase() === tokenLower)
      );
      if (exactMatch) {
        setExactMatchSubject({ id: exactMatch.id, name: exactMatch.name });
        setSelectedSubjectId(exactMatch.id);
        setSide("alias");
      } else {
        setExactMatchSubject(null);
      }

      setSubjectSuggestions(sorted);

      const allBackgrounds = await listBackgrounds();
      const backgrounds: BackgroundSuggestion[] = allBackgrounds.map((b) => ({
        background_id: b.id,
        name: b.name,
        code: b.code,
        similarity_score: 0,
        is_preselected: false,
        is_alias: false,
      }));
      setBackgroundSuggestions(backgrounds);
    } catch (err) {
      console.error("Failed to load suggestions:", err);
      try {
        const subjects = await tokenResolutionApi.getSubjectSuggestions(token);
        setSubjectSuggestions(subjects || []);
        const backgrounds =
          await tokenResolutionApi.getBackgroundSuggestions(token);
        setBackgroundSuggestions(backgrounds || []);
      } catch {
        // Silent fail
      }
    } finally {
      setLoadingSubjects(false);
      setLoadingBackgrounds(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, allTokens.length]);

  useEffect(() => {
    if (open) {
      setSubjectName(token);
      setSubjectQuery("");
      setSelectedSubjectId(null);
      setSelectedBackgroundIds(new Set());
      setExactMatchSubject(null);
      setError(null);
      setPdfError(null);
      setPdfOpen(false);
      setPdf(null);
      setSide("alias");
      loadSuggestions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, token]);

  const filteredSubjects = useMemo(() => {
    const q = subjectQuery.trim().toLowerCase();
    if (!q) return subjectSuggestions;
    return subjectSuggestions.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        (s.code || "").toLowerCase().includes(q)
    );
  }, [subjectSuggestions, subjectQuery]);

  const selectedBackgrounds = useMemo(
    () =>
      (backgroundSuggestions || []).filter((b) =>
        selectedBackgroundIds.has(b.background_id)
      ),
    [backgroundSuggestions, selectedBackgroundIds]
  );

  const toggleBackground = useCallback((backgroundId: string) => {
    setSide("new");
    setSelectedBackgroundIds((prev) => {
      const next = new Set(prev);
      if (next.has(backgroundId)) next.delete(backgroundId);
      else next.add(backgroundId);
      return next;
    });
  }, []);

  const handleCreateAlias = useCallback(async () => {
    if (!selectedSubjectId) return;
    if (
      exactMatchSubject &&
      selectedSubjectId === exactMatchSubject.id &&
      allTokens.length === 1 &&
      token.toLowerCase() === exactMatchSubject.name.toLowerCase()
    ) {
      onResolve("exact_match", {
        subject_id: exactMatchSubject.id,
        subject_name: exactMatchSubject.name,
        canonical_subject_id: exactMatchSubject.id,
      });
      onClose();
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      for (const aliasName of allTokens) {
        await tokenResolutionApi.createSubjectAlias({
          alias_name: aliasName,
          canonical_subject_id: selectedSubjectId,
        });
      }

      const chosen = subjectSuggestions.find(
        (s) => s.subject_id === selectedSubjectId
      );
      onResolve("alias", {
        alias_name: token,
        canonical_subject_id: selectedSubjectId,
        subject_id: selectedSubjectId,
        subject_name: chosen?.name,
        tokens: allTokens,
      });
      onClose();
    } catch (err) {
      setError(
        extractErrorMessage(err, {
          loadError: "Alias konnte nicht gespeichert werden",
          saveError: "Alias konnte nicht gespeichert werden",
        })
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    token,
    allTokens,
    selectedSubjectId,
    exactMatchSubject,
    subjectSuggestions,
    onResolve,
    onClose,
  ]);

  const handleCreateSubject = useCallback(async () => {
    if (exactMatchSubject) return;
    const selectedIds = Array.from(selectedBackgroundIds);
    if (!subjectName.trim() || selectedIds.length === 0) return;

    setSubmitting(true);
    setError(null);
    try {
      const created = await tokenResolutionApi.createSubjectWithBackground(
        {
          subject_name: subjectName.trim(),
          qualification_type: "qualified",
          background_id: selectedIds[0],
          background_ids: selectedIds,
        },
        sessionId
      );

      if (created?.subject_id) {
        const subjectNameLower = subjectName.trim().toLowerCase();
        for (const aliasName of allTokens) {
          if (aliasName.toLowerCase() === subjectNameLower) continue;
          await tokenResolutionApi.createSubjectAlias({
            alias_name: aliasName,
            canonical_subject_id: created.subject_id,
          });
        }
      }

      onResolve("new_subject", {
        subject_name: subjectName.trim(),
        subject_id: created?.subject_id,
        canonical_subject_id: created?.subject_id,
        background_ids: selectedIds,
        tokens: allTokens,
      });
      onClose();
    } catch (err) {
      setError(
        extractErrorMessage(err, {
          loadError: "Fach konnte nicht angelegt werden",
          saveError: "Fach konnte nicht angelegt werden",
        })
      );
    } finally {
      setSubmitting(false);
    }
  }, [
    subjectName,
    selectedBackgroundIds,
    sessionId,
    allTokens,
    exactMatchSubject,
    onResolve,
    onClose,
  ]);

  if (!open) return null;

  const pdfTotal = sessionIds.length || 1;
  const pdfLabel = pdf ? sourceLabel(pdf, occurrence, pdfTotal) : null;

  const aliasReady = !!selectedSubjectId;
  const newReady =
    !!subjectName.trim() &&
    selectedBackgroundIds.size > 0 &&
    !exactMatchSubject;
  const saveDisabled =
    submitting || (side === "alias" ? !aliasReady : !newReady);
  const showMatrixHint =
    hasQualificationMatrix || backgroundSuggestions.length > 0;

  return (
    <>
      <Modal
        isOpen={open}
        onClose={pdfOpen ? () => setPdfOpen(false) : onClose}
        title={t(
          "unmatchedSubjectDialog_title",
          "Unbekanntes Fach: {{token}}",
          { token: allTokens.length > 1 ? `${allTokens.length} Token` : token }
        )}
        size="xl"
      >
        <div className="space-y-4">
          {exactMatchSubject && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-md">
              <p className="text-sm text-amber-800">
                Das Fach <strong>"{exactMatchSubject.name}"</strong> existiert
                bereits in der Qualifikationsmatrix. Erstelle einen Alias, um "
                {token}" diesem Fach zuzuordnen.
              </p>
            </div>
          )}

          <div className="grid gap-3 md:grid-cols-2">
            <div
              onClick={() => setSide("alias")}
              className={`text-left rounded-lg border p-3 ${
                side === "alias"
                  ? "border-blue-600 bg-blue-50/60 ring-2 ring-blue-200"
                  : "border-gray-200 bg-white"
              }`}
            >
              <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
                {t("unmatchedSubjectDialog_tabAlias", "Alias zuordnen")}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                {t(
                  "unmatchedSubjectDialog_aliasDescription",
                  'Ordnen Sie "{{token}}" einem bestehenden Fach als Alias zu.',
                  { token }
                )}
              </p>
              <p className="mt-2 text-xs text-emerald-700">
                Matrix unverändert.
              </p>
              {loadingSubjects ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
                  <span className="ml-2 text-gray-500">
                    {t("unmatchedSubjectDialog_loading", "Vorschläge laden...")}
                  </span>
                </div>
              ) : (
                <div
                  className="mt-3 space-y-2"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                    value={subjectQuery}
                    onChange={(e) => {
                      setSide("alias");
                      setSubjectQuery(e.target.value);
                    }}
                    onFocus={() => setSide("alias")}
                    placeholder={t(
                      "unmatchedSubjectDialog_searchSubject",
                      "Fach suchen"
                    )}
                  />
                  <div className="max-h-48 overflow-y-auto border rounded-md bg-white">
                    {filteredSubjects.length === 0 ? (
                      <div className="p-4 text-center text-gray-500 text-sm">
                        {t(
                          "unmatchedSubjectDialog_noSuggestions",
                          "Keine Vorschläge gefunden"
                        )}
                      </div>
                    ) : (
                      filteredSubjects.map((suggestion) => (
                        <button
                          key={suggestion.subject_id}
                          type="button"
                          className={`w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 ${
                            selectedSubjectId === suggestion.subject_id
                              ? "bg-blue-50 border-l-2 border-blue-500"
                              : ""
                          }`}
                          onClick={() => {
                            setSide("alias");
                            setSelectedSubjectId(suggestion.subject_id);
                          }}
                        >
                          {suggestion.is_preselected && (
                            <Check className="h-4 w-4 text-blue-500" />
                          )}
                          <span className="font-medium">{suggestion.name}</span>
                          {suggestion.code && (
                            <Badge className="text-xs bg-gray-100 text-gray-600">
                              {suggestion.code}
                            </Badge>
                          )}
                          <span className="text-xs text-gray-400 ml-auto">
                            {suggestion.similarity_score}%
                          </span>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>

            <div
              onClick={() => {
                if (!exactMatchSubject) setSide("new");
              }}
              className={`text-left rounded-lg border p-3 ${
                exactMatchSubject
                  ? "border-gray-200 bg-gray-50 opacity-60"
                  : side === "new"
                    ? "border-blue-600 bg-blue-50/60 ring-2 ring-blue-200"
                    : "border-gray-200 bg-white"
              }`}
            >
              <p className="text-[11px] font-bold uppercase tracking-wide text-gray-500">
                {t("unmatchedSubjectDialog_tabNew", "Neues Fach erstellen")}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                {showMatrixHint
                  ? "Neues Fach. Danach Pflicht: ein Hintergrund in der Matrix."
                  : "Neues Fach. Sobald Hintergründe existieren, muss eine Matrix-Zelle gesetzt werden."}
              </p>
              <div
                className="mt-3 space-y-2"
                onClick={(e) => e.stopPropagation()}
              >
                <label className="text-sm font-medium text-gray-700">
                  {t("unmatchedSubjectDialog_subjectName", "Fach-Name")}
                </label>
                <input
                  className="w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                  value={subjectName}
                  disabled={!!exactMatchSubject}
                  onChange={(e) => {
                    setSide("new");
                    setSubjectName(e.target.value);
                  }}
                  onFocus={() => {
                    if (!exactMatchSubject) setSide("new");
                  }}
                />
                <p className="text-sm font-medium text-gray-700">
                  {t(
                    "unmatchedSubjectDialog_selectBackground",
                    "Mindestens ein Hintergrund"
                  )}
                </p>
                {loadingBackgrounds ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto">
                    {backgroundSuggestions.map((suggestion) => {
                      const on = selectedBackgroundIds.has(
                        suggestion.background_id
                      );
                      return (
                        <button
                          key={suggestion.background_id}
                          type="button"
                          disabled={!!exactMatchSubject}
                          className={`rounded-full border px-2.5 py-1 text-xs ${
                            on
                              ? "border-emerald-400 bg-emerald-50 text-emerald-800 font-semibold"
                              : "border-gray-300 bg-white text-gray-700"
                          }`}
                          onClick={() =>
                            toggleBackground(suggestion.background_id)
                          }
                        >
                          {suggestion.code || suggestion.name}
                        </button>
                      );
                    })}
                  </div>
                )}
                <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 px-2 py-2 text-xs text-gray-600">
                  Neue Spalte <strong>{subjectName.trim() || token}</strong>
                  <br />
                  Zelle:{" "}
                  {selectedBackgrounds.length
                    ? `Qualifiziert · ${selectedBackgrounds.map((b) => b.name).join(", ")}`
                    : "—"}
                </div>
                {side === "new" && selectedBackgroundIds.size === 0 && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                    Ohne Hintergrund entsteht ein Fach ohne Matrix-Spalte.
                    Speichern bleibt gesperrt.
                  </p>
                )}
              </div>
            </div>
          </div>

          {error ? <p className="text-sm text-red-600">{error}</p> : null}
          {pdfError ? <p className="text-sm text-red-600">{pdfError}</p> : null}
          {pdfLabel && !pdfOpen ? (
            <p className="text-xs text-gray-600 truncate" title={pdfLabel}>
              {pdfLabel}
            </p>
          ) : null}

          <div className="flex justify-end gap-2 pt-2">
            {canPreviewPdf ? (
              <Button
                variant="secondary"
                className="mr-auto"
                onClick={() => {
                  const sessions = (positions?.sessions || []).filter(Boolean);
                  const last = sessions[sessions.length - 1];
                  const index = last
                    ? Math.max(0, sessionIds.indexOf(last))
                    : 0;
                  void loadPdfAt(index);
                }}
                disabled={pdfLoading}
                title="Stundenplan anzeigen, in dem dieses Fach vorkommt"
              >
                {pdfLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Eye className="h-4 w-4 mr-2" />
                )}
                {t("unmatchedSubjectDialog_pdfPreview", "PDF-Vorschau")}
                {sessionIds.length > 1 ? ` (${sessionIds.length})` : ""}
              </Button>
            ) : null}
            <span
              className={`text-xs text-gray-500 self-center ${canPreviewPdf ? "" : "mr-auto"}`}
            >
              {side === "alias"
                ? "Alias · Matrix unverändert"
                : selectedBackgroundIds.size
                  ? `${selectedBackgroundIds.size} Hintergrund gesetzt`
                  : "Hintergrund fehlt"}
            </span>
            <Button variant="secondary" onClick={onClose}>
              {t("unmatchedSubjectDialog_cancel", "Abbrechen")}
            </Button>
            <Button
              onClick={
                side === "alias" ? handleCreateAlias : handleCreateSubject
              }
              disabled={saveDisabled}
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {side === "alias"
                ? t("unmatchedSubjectDialog_createAlias", "Als Alias zuordnen")
                : t("unmatchedSubjectDialog_create", "Neues Fach anlegen")}
            </Button>
          </div>
        </div>
      </Modal>
      {pdfOpen && pdf ? (
        <PdfPageViewer
          pdfBase64={pdf.pdf_base64}
          pageNumber={pdf.page_number}
          totalPages={pdf.total_pages}
          open={pdfOpen}
          subtitle={pdfLabel || undefined}
          onClose={() => setPdfOpen(false)}
          onPrev={
            pdfTotal > 1 ? () => void loadPdfAt(occurrence - 1) : undefined
          }
          onNext={
            pdfTotal > 1 ? () => void loadPdfAt(occurrence + 1) : undefined
          }
          prevDisabled={occurrence <= 0 || pdfLoading}
          nextDisabled={occurrence >= pdfTotal - 1 || pdfLoading}
        />
      ) : null}
    </>
  );
}
